import React, { useCallback, useMemo, useRef, useState } from "react";
import WebView from "./WebView";
import { Modal, Platform, StatusBar, Text, TouchableOpacity, View } from "react-native";
import { PlatformOSType } from "react-native/Libraries/Utilities/Platform";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import VStack from "../ui_atoms/VStack";
import { useTheme } from "../hooks/use-theme";
import Constants from "expo-constants";
import { useI18n } from "../hooks/i18n-hooks";
import Icon from "./Icon";
import * as FileSystem from "expo-file-system";
import * as Sharing from "expo-sharing";
import LoadingModal from "./LoadingModal";
import { isStorybookMode } from "../sherlo";
import { KeyboardAvoidingView } from "react-native-keyboard-controller";

const appEnv = Constants.expoConfig?.extra?.appEnv;

type WebViewWrapperProps = {
    url?: string | null;
    id?: string;
    onUrlChanged: (url: string) => void;
    getDownloadFileName?: (url: string) => string;
    enableBack?: boolean;
};

const SAFARI_VERSION = "604.1";
const USER_AGENTS: Record<PlatformOSType, () => string | undefined> = {
    ios: () =>
        `Mozilla/5.0 (iPhone; CPU iPhone OS 16_4_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/${Platform.Version} Mobile/15E148 Safari/${SAFARI_VERSION}`,
    android: () => undefined,
    windows: () => undefined,
    macos: () => undefined,
    web: () => undefined,
    native: () => undefined,
};

const WebViewWrapper: React.FC<WebViewWrapperProps> = ({
    url,
    id,
    onUrlChanged,
    enableBack,
    getDownloadFileName,
}) => {
    const userAgent = USER_AGENTS[Platform.OS]();
    const safeAreaInsets = useSafeAreaInsets();
    const [hasBack, setHasBack] = useState<boolean>(false);
    const [isSharing, setIsSharing] = useState(false);

    const webviewRef = useRef<WebView | null>(null);
    const { colors, fonts } = useTheme();
    const i18n = useI18n();
    const offset = 5;
    const inOffset = 5;
    const backSize = 36;
    const handleGoBack = useCallback(() => {
        if (hasBack) {
            webviewRef.current?.goBack();
        } else {
            onUrlChanged("__cancel__");
        }
    }, [hasBack, onUrlChanged, webviewRef]);

    const downloadFile = useCallback(
        (url: string) => {
            const fn = async () => {
                try {
                    setIsSharing(true);
                    let fileName = getDownloadFileName?.(url);
                    if (!fileName) {
                        const urlObj = new URL(url);
                        fileName = urlObj.pathname.split("/").pop() || "file.bin";
                    }
                    fileName = fileName.replaceAll(" ", "_");
                    const localUri = `${FileSystem.documentDirectory || "/"}${fileName}`;
                    const downloadRet = await FileSystem.downloadAsync(url, localUri);
                    await Sharing.shareAsync(downloadRet.uri);
                } finally {
                    setIsSharing(false);
                }
            };
            void fn();
        },
        [getDownloadFileName],
    );
    const content = useMemo(() => {
        return url ? (
            <VStack style={{ flex: 1 }}>
                <LoadingModal visible={isSharing} />
                <StatusBar barStyle="dark-content" />
                <View
                    style={{
                        height: isStorybookMode ? 60 : safeAreaInsets.top, // storybook do not have safe area
                        backgroundColor: colors.surface,
                        width: "100%",
                    }}
                />
                {enableBack && (
                    <View
                        style={{
                            height: backSize + inOffset * 2,
                            backgroundColor: colors.surface,
                            paddingHorizontal: offset,
                            borderBottomWidth: 1,
                            borderBottomColor: colors.accentLighter,
                        }}
                    >
                        <TouchableOpacity
                            onPress={handleGoBack}
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                            }}
                        >
                            <Icon
                                name={hasBack ? "back" : "close"}
                                size={28}
                                style={{
                                    margin: inOffset,
                                }}
                                color={colors.primary}
                            />
                            <Text
                                style={{
                                    color: colors.primary,
                                    marginLeft: offset,
                                    ...fonts.bodyLarge,
                                }}
                            >
                                {i18n.t(hasBack ? "back" : "close")}
                            </Text>
                        </TouchableOpacity>
                    </View>
                )}
                <WebView
                    ref={webviewRef}
                    id={id}
                    style={{ flex: 1 }}
                    source={{ uri: url }}
                    userAgent={userAgent}
                    allowsFullscreenVideo={true}
                    allowsInlineMediaPlayback={true}
                    mediaPlaybackRequiresUserAction={false}
                    webviewDebuggingEnabled={appEnv !== "production"}
                    setSupportMultipleWindows={false}
                    originWhitelist={["*"]}
                    onHttpError={(event) => {
                        // do not uses error, it will trigger an unicorn
                        console.warn("onHttpError", event);
                    }}
                    onNavigationStateChange={(event) => {
                        setHasBack(event.canGoBack);
                        if (!event.loading) {
                            if (event.url.startsWith("blob:")) {
                                // DO NOT WORK ON ANDROID
                                // https://github.com/react-native-webview/react-native-webview/pull/3022
                                if (!isSharing) {
                                    void downloadFile(event.url.substring(5));
                                }
                            } else {
                                onUrlChanged(event.url);
                            }
                        }
                    }}
                />
            </VStack>
        ) : null;
    }, [
        url,
        safeAreaInsets.top,
        colors.surface,
        colors.accentLighter,
        colors.primary,
        enableBack,
        handleGoBack,
        hasBack,
        fonts.bodyLarge,
        i18n,
        id,
        userAgent,
        isSharing,
        downloadFile,
        onUrlChanged,
    ]);
    if (Constants.expoConfig?.extra?.displayStorybook) {
        return <View style={{ flex: 1, backgroundColor: colors.background }}>{content}</View>;
    }
    return (
        <Modal visible={!!url} animationType="slide" statusBarTranslucent={true}>
            <KeyboardAvoidingView style={{ flex: 1 }} behavior="padding">
                {content}
            </KeyboardAvoidingView>
        </Modal>
    );
};

export default WebViewWrapper;
